/**
 * ExpenseLedgerContext (Phase D) — READ-ONLY in-memory mirror of the
 * canonical `expense_ledger` table (db/migrations/0003_expense_ledger.sql).
 *
 * Frontend prototype scope:
 *   - No writer. No editing UI. No mutation API exposed.
 *   - Entries are seeded once on mount and never mutated for the life of
 *     the session. This mirrors the DB's append-only contract.
 *   - Role gating happens in `useJobExpenseLedger()` — the raw provider
 *     value is finance-only; UI consumers MUST go through the hook.
 *
 * When a real Edge Function reader (`expense-ledger-read`) is added later,
 * the seed array is replaced with a fetch — context shape stays stable.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ExpenseLedgerEntry } from "@/data/jobCost/expenseLedgerBridge";
import { getExpenseLedgerEntriesByJob } from "@/data/jobCost/expenseLedgerReader";

interface ExpenseLedgerContextValue {
  /** Full in-memory ledger. Treat as immutable. */
  entries: ReadonlyArray<ExpenseLedgerEntry>;
  /** Pure selector — does NOT enforce role gates (use the hook for that). */
  getEntriesByJob: (jobId: string) => ExpenseLedgerEntry[];
}

const Ctx = createContext<ExpenseLedgerContextValue | null>(null);

export interface ExpenseLedgerProviderProps {
  children: ReactNode;
  /** Test/Storybook override. Defaults to empty ledger. */
  seedEntries?: ExpenseLedgerEntry[];
}

export function ExpenseLedgerProvider({
  children,
  seedEntries,
}: ExpenseLedgerProviderProps) {
  const value = useMemo<ExpenseLedgerContextValue>(() => {
    const entries = seedEntries ?? [];
    return {
      entries,
      getEntriesByJob: (jobId: string) =>
        getExpenseLedgerEntriesByJob(entries, jobId),
    };
  }, [seedEntries]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useExpenseLedgerContext(): ExpenseLedgerContextValue {
  const v = useContext(Ctx);
  if (!v) {
    // Graceful fallback — components outside the provider see an empty
    // ledger (Phase A invariant: missing ≠ zero, panel keeps missing rows).
    return {
      entries: [],
      getEntriesByJob: () => [],
    };
  }
  return v;
}
